/**
 * Font inspection mode for reviewing typography.
 *
 * Off for every visitor by default. Add `?fonts=1` to any URL to turn it on
 * for the current browser tab (it follows you from page to page), and
 * `?fonts=0` to turn it off again.
 *
 * When on, every word gets a small lowercase label naming the font that is
 * actually rendering it: the first family in the element's font stack that
 * has loaded, or the generic fallback (serif, sans-serif…) if none has.
 */
(() => {
  const KEY = 'norel-font-labels';
  const param = new URLSearchParams(location.search).get('fonts');

  try {
    if (param === '1') sessionStorage.setItem(KEY, '1');
    if (param === '0') sessionStorage.removeItem(KEY);
    if (sessionStorage.getItem(KEY) !== '1') return;
  } catch {
    if (param !== '1') return;
  }

  const LABEL_CLASS = 'norel-font-label';
  const SKIP = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION', 'SVG', 'CODE', 'PRE']);
  const GENERIC = new Set(['serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui', 'ui-serif', 'ui-sans-serif', 'ui-monospace', '-apple-system']);
  const cache = new Map();

  const style = document.createElement('style');
  style.textContent = `
    .${LABEL_CLASS} {
      display: inline-block;
      margin-inline: 0.15em 0.35em;
      padding: 0 0.3em;
      border-radius: 3px;
      background: color-mix(in srgb, var(--color-danger) 10%, transparent);
      color: var(--color-danger);
      font: 500 9px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace !important;
      letter-spacing: 0 !important;
      text-transform: none !important;
      vertical-align: super;
      white-space: nowrap;
      pointer-events: none;
    }`;
  document.head.append(style);

  /** Lowercase name of the font actually used for an element's text. */
  function renderedFont(element) {
    const stack = getComputedStyle(element).fontFamily;
    const weight = getComputedStyle(element).fontWeight;
    const cacheKey = `${stack}|${weight}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    const families = stack.split(',').map((family) => family.trim().replace(/^["']|["']$/g, ''));
    let name = families[families.length - 1] || 'unknown';
    for (const family of families) {
      if (GENERIC.has(family.toLowerCase())) {
        name = family;
        break;
      }
      if (document.fonts.check(`${weight} 16px "${family}"`)) {
        const loaded = [...document.fonts].some(
          (face) => face.family.replace(/["']/g, '') === family && face.status === 'loaded'
        );
        if (loaded) {
          name = family;
          break;
        }
      }
    }

    name = name.toLowerCase();
    cache.set(cacheKey, name);
    return name;
  }

  function shouldSkip(node) {
    for (let el = node.parentElement; el; el = el.parentElement) {
      if (SKIP.has(el.tagName.toUpperCase()) || el.classList.contains(LABEL_CLASS)) return true;
    }
    return false;
  }

  function label(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) =>
        node.nodeValue.trim() && !shouldSkip(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    for (const node of nodes) {
      const parent = node.parentElement;
      if (!parent || parent.getClientRects().length === 0) continue;

      const font = renderedFont(parent);
      const fragment = document.createDocumentFragment();
      for (const token of node.nodeValue.split(/(\s+)/)) {
        if (!token) continue;
        fragment.append(token);
        if (/[\p{L}\p{N}]/u.test(token)) {
          const tag = document.createElement('span');
          tag.className = LABEL_CLASS;
          tag.setAttribute('aria-hidden', 'true');
          tag.textContent = font;
          fragment.append(tag);
        }
      }
      node.replaceWith(fragment);
    }
  }

  // Wait for web fonts so labels name the real font, not the fallback.
  const run = () => document.fonts.ready.then(() => label(document.body));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
